import { Component, ElementRef, EventEmitter, HostListener, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, Subscriber, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { BcbService } from 'src/app/services/bcb/bcb.service';
import { ReferentielService } from 'src/app/services/referentiel/referentiel.service';
import { Referentiel } from 'src/app/utils/interfaces/new/referentiel.interface';
import { environment } from 'src/environments/environment';

export enum ReferentielType {
  "obs-intolerance-type" = 'RefObsIntoleranceType',
  "act-status" = 'RefActStatus',
  "problem-code" = 'RefProblemCode',
  "personal-relationship-role-type" = 'RefPersonalRelationshipRoleType',
  "social-history-codes" = 'RefSocialHistoryCodes',
  "statut-tabagique" = 'RefStatutTabagique',
  "cim-10" = 'CIM10',
  "cisp-2" = 'cisp',
  "ccam" = 'RefActeCcam',
  "atc" = 'RefAtc',
  "produitMed" = 'produitMed',
  "allergies-vaccins" = 'AllergiesVaccins',
  "snomed-allergene" = 'SnomedAllergenes',
  "allergene-bcb" = 'allergene-bcb',
}

const ReferentielsMap = (): Map<string, ReferenceTemplateType> => {
  const map = new Map<string, ReferenceTemplateType>();

  map.set('RefObsIntoleranceType', { id: 'RefObsIntoleranceType', libelle: 'Type d\'intolérence observée', systemCode: '2.16.840.1.113883.1.11.19700' });
  map.set('RefActStatus', { id: 'RefActStatus', libelle: 'Statut de l\'acte', systemCode: '2.16.840.1.113883.1.11.15933' });
  map.set('RefProblemCode', { id: 'RefProblemCode', libelle: 'Code problème', systemCode: '1.2.250.1.213.1.1.5.172' });
  map.set('RefPersonalRelationshipRoleType', { id: 'RefPersonalRelationshipRoleType', libelle: 'Relation personnelle', systemCode: '2.16.840.1.113883.1.11.19563' });
  map.set('RefSocialHistoryCodes', { id: 'RefSocialHistoryCodes', libelle: 'Habitus', systemCode: '1.2.250.1.213.1.1.4.2.283.4' });
  map.set('RefStatutTabagique', { id: 'RefStatutTabagique', libelle: 'Statut de consommation de tabac', systemCode: '2.16.840.1.113883.6.1' });
  map.set('CIM10', { id: 'CIM10', libelle: 'CIM-10', systemCode: '2.16.840.1.113.883.6' });
  map.set('cisp', { id: 'cisp', libelle: 'CISP 2', systemCode: '2.16.840.1.113883.6.138' });
  map.set('RefActeCcam', { id: 'RefActeCcam', libelle: 'CCAM', systemCode: '1.2.250.1.213.2.5' });
  map.set('RefAtc', { id: 'RefAtc', libelle: 'ATC', systemCode: '2.16.840.1.113883.6.73' });
  map.set('produitMed', { id: 'produitMed', libelle: 'Produits médicaux', systemCode: '1.2.250.1.213.2.3.1' });
  map.set('AllergiesVaccins', { id: 'AllergiesVaccins', libelle: 'Produits allergènes vaccins', systemCode: '1.2.250.1.213.1.1.5.100' });
  map.set('SnomedAllergenes', { id: 'SnomedAllergenes', libelle: 'Produits environnementaux (SNOMED)', systemCode: '1.2.250.1.213.2.12' });
  map.set('allergene-bcb', { id: 'allergene-bcb', libelle: 'Allergènes produits médicaux (depuis BCB)', systemCode: 'allergene-bcb' });

  return map;
};

interface ReferenceTemplateType {
  libelle: string;
  id: string;
  systemCode: string;
}

export interface Result {
  id: string;
  libelle: string;
  systemCode: string;
}

@Component({
  selector: 'app-recherche-referentiel',
  templateUrl: './recherche-referentiel.component.html',
  styleUrls: [
    './recherche-referentiel.component.scss',
    '../modal.fragment.scss'
  ]
})
export class RechercheReferentielComponent implements OnInit, OnDestroy {
  @ViewChild('modalBody') public modalBody: ElementRef;
  @ViewChild('outsideModal') public outsideModal: ElementRef;

  @Output() public closeEmitter = new EventEmitter<Result | undefined>();

  @HostListener('document:mousedown', ['$event'])
  onGlobalClick(event): void {
    if (!environment.closeModalOnClickOutside) {
      return;
    }

    if (!this.modalBody.nativeElement.contains(event.target) && this.outsideModal.nativeElement.contains(event.target)) {
      this.closeModal();
    }
  }

  private _enableRefs: Array<ReferenceTemplateType>;
  public set enableRefs(refs: Array<ReferentielType> | undefined) {
    if (!this._enableRefs) {
      this._enableRefs = [];
    }

    if (refs) {
      for (const ref of refs) {
        const refMapGet = this._refMap.get(ref);
        if (refMapGet) {
          this._enableRefs.push(refMapGet);
        }
      }
    } else {
      for (const ref of Object.values(ReferentielType)) {
        const refMapGet = this._refMap.get(ref);
        if (refMapGet) {
          this._enableRefs.push(refMapGet);
        }
      }
    }
  }
  public get getEnableRefs(): Array<ReferenceTemplateType> {
    return this._enableRefs || [];
  }

  private __refMap: Map<string, ReferenceTemplateType>;
  private set _refMap(_) {
    if (!this.__refMap) {
      this.__refMap = ReferentielsMap();
    }
  }
  private get _refMap(): Map<string, ReferenceTemplateType> {
    if (!this.__refMap) {
      this._refMap = undefined;
    }
    return this.__refMap;
  }

  public formGroup: FormGroup;
  public error: string;
  public searchResult: Array<Result>;
  public selectedResult: Result;
  private _searchSub: Subscriber<{search: string, selectedRef: ReferentielType}>;
  private _searchObs: Observable<{search: string, selectedRef: ReferentielType}> = new Observable<{search: string, selectedRef: ReferentielType}>(obs => { this._searchSub = obs });
  private _searchObsSub: Subscription;

  constructor(
    private _formBuilder: FormBuilder,
    private _refSrv: ReferentielService,
    private _bcbService: BcbService,
  ) {
    this._searchObsSub = this._searchObs.pipe(debounceTime(500)).subscribe(async v => {
      this.selectedResult = undefined;
      this.error = undefined;

      if (v.search.length < 3) {
        return;
      }

      let result: Array<Referentiel>;

      if (v.selectedRef === ReferentielType['cisp-2']) {
        // TODO Ajouter une option de recherche sur les terminologies
        // result = await (await this._refSrv.findTerminologie(v.selectedRef, v.search));
        result = await (await this._refSrv.findTerminologie(v.selectedRef));
      } else if (v.selectedRef === ReferentielType['produitMed']) {
        const produits = await this._bcbService.searchProduitByNom(v.search);
        result = produits.map(p => {
          return { id: p.idProduit || '', libelle: p.libelle || '' };
        });
      } else {
        let table: ReferentielType | string = v.selectedRef;
        if (table === ReferentielType['allergene-bcb']) {
          table = 'RefAllergie';
        }
        result = await this._refSrv.findRefListTableHypermed(table, v.search);
      }

      if (result && result.length > 0) {
        this.searchResult = result.splice(0, result.length).map(r => {
          return { id: r.id, libelle: this._getReferentielLibelle(r, v?.selectedRef), systemCode: this._refMap.get(v.selectedRef)?.systemCode };
        });
      } else {
        this.searchResult = [];
      }
    });
  }

  private _getReferentielLibelle(referentiel: Referentiel, referentielType?: ReferentielType): string {
    if (referentielType) {
      if (
        referentielType === ReferentielType['cim-10']
        || referentielType === ReferentielType.ccam
      ) {
        return `${referentiel.id} — ${referentiel.libelle}`
      }
    }
    return referentiel.libelle;
  }

  ngOnInit(): void {
    this._initForm();
  }

  ngOnDestroy(): void {
    if (this._searchObsSub) {
      this._searchObsSub.unsubscribe();
    }

    if (this._searchSub) {
      this._searchSub.unsubscribe();
    }
  }

  private _initForm(): void {
    this.formGroup = this._formBuilder.group({
      selectedRef: [this.getEnableRefs && this.getEnableRefs.length > 0 ? this.getEnableRefs[0].id : '', Validators.required],
      search: ['']
    });
  }

  public async search(): Promise<void> {
    const selectedRef = this.formGroup && this.formGroup.get('selectedRef') && this.formGroup.get('selectedRef').value
      ? this.formGroup.get('selectedRef').value : undefined;
    const search = this.formGroup && this.formGroup.get('search') &&  this.formGroup.get('search').value
      ? this.formGroup.get('search').value : undefined;

    if (!selectedRef || selectedRef === '') {
      this.error = 'Une référence/terminologie est obligatoire pour lancer une recherche';
      return;
    }

    if (!search || search === '') {
      this.error = 'Un mot clé est obligatoire pour lancer une recherche';
      return;
    }

    this._searchSub.next({ search, selectedRef });
  }

  public selectResult(result: Result): void {
    if (this.selectedResult && this.selectedResult.id === result.id) {
      this.selectedResult = undefined;
    } else {
      this.selectedResult = result;
    }
  }

  public onSubmit(): any {
    this.closeModal(this.selectedResult);
  }

  public closeModal(object?: Result): void {
    if (object) {
      this.closeEmitter.emit(object);
    } else {
      this.closeEmitter.emit(null);
    }
  }

}
